import { Fragment, memo, useMemo } from 'react';
import { cn } from '@clarix/ui/lib/cn';
import { useSearchHighlightQuery } from './SearchHighlightContext';

interface HighlightTextProps {
  /**
   * The full text to render. Pass the cell value verbatim — never the
   * search-stripped form.
   */
  text: string | null | undefined;
  /**
   * The active search term. Omit it inside a `DataTable` with `searchQuery`, or
   * any `SearchHighlightProvider` — the term is read from context. Pass it
   * explicitly to override that (or on a surface with no provider).
   */
  query?: string | null;
  /** Fallback when `text` is empty / null / undefined. Defaults to `'-'`. */
  fallback?: string;
  /** Extra classes for the outer wrapper. Only renders a wrapper when set. */
  className?: string;
  /**
   * Extra classes for each matched fragment. The shared yellow from
   * `.search-highlight` always applies — pass this only to add to it (spacing,
   * weight), never to recolour. The highlight colour is fixed across every app.
   */
  highlightClassName?: string;
}

const ESCAPE_REGEX = /[.*+?^${}()|[\]\\]/g;

function buildPattern(query: string): RegExp | null {
  const tokens = query
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => t.replace(ESCAPE_REGEX, '\\$&'));
  if (tokens.length === 0) return null;
  // Longest tokens first so multi-word substrings like "head office" win over a
  // stray "of" match — otherwise the regex chunks the longer match into smaller
  // hits and the highlight looks fragmented.
  tokens.sort((a, b) => b.length - a.length);
  return new RegExp(`(${tokens.join('|')})`, 'gi');
}

/**
 * HighlightText — marks the matched query substring inside a cell.
 *
 * Use everywhere searchable content renders — table cells, list rows, combobox
 * dropdowns — so the user can see WHY a row is in the result set without
 * scanning every column. No-ops (returns the raw text) when `query` is empty, so
 * it is safe to drop in unconditionally. Multi-token queries highlight each
 * token; matching is case-insensitive.
 */
function HighlightTextImpl({
  text,
  query,
  fallback = '-',
  className,
  highlightClassName,
}: HighlightTextProps) {
  const value = text ?? '';
  // An explicit `query` wins; otherwise inherit the surrounding search term.
  // `query === undefined` means "not specified" — `null` / '' are treated as a
  // deliberate opt-out, so a nested surface can suppress the page's term.
  const contextQuery = useSearchHighlightQuery();
  const normalizedQuery = (query === undefined ? contextQuery : (query ?? '')).trim();

  const segments = useMemo(() => {
    if (!value || !normalizedQuery) return null;
    const pattern = buildPattern(normalizedQuery);
    if (!pattern) return null;
    const parts = value.split(pattern);
    return parts.length > 1 ? parts : null;
  }, [value, normalizedQuery]);

  const wrap = (content: React.ReactNode) =>
    className ? <span className={className}>{content}</span> : <>{content}</>;

  if (!value) return wrap(fallback);
  if (!segments) return wrap(value);

  // `.search-highlight` owns the colour (packages/ui/src/styles.css) and is
  // always applied. highlightClassName is additive — for spacing or weight, not
  // colour; the yellow is deliberately identical on every search surface.
  const markCls = cn('search-highlight', highlightClassName);
  const pattern = buildPattern(normalizedQuery);

  return wrap(
    segments.map((part, idx) => {
      if (!part) return null;
      const isMatch = pattern?.test(part) ?? false;
      // Reset lastIndex — the `g` flag makes .test() stateful across calls, so
      // without this every second match would be skipped.
      if (pattern) pattern.lastIndex = 0;
      return (
        <Fragment key={`${idx}-${part}`}>
          {isMatch ? <mark className={markCls}>{part}</mark> : part}
        </Fragment>
      );
    }),
  );
}

export const HighlightText = memo(HighlightTextImpl);
